import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { LoadingBlock } from '@components/LoadingBlock';
import { STORAGE_KEY, getStoredLocale } from '../../i18n/config';

/**
 * Layout component for all /:lang/* broker routes. Handles:
 * - Language sync (i18n + dir + localStorage)
 * - authLoading → full-screen loader
 * - Invalid lang param → redirect preserving segment
 * - session_expired toast on /:lang/signup?reason=session_expired
 */
export function BrokerRoot() {
  const { lang: langParam } = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { authLoading } = useAuth();
  const { addToast } = useToast();
  const sessionExpiredToastShown = useRef(false);

  useEffect(() => {
    if (langParam !== 'en' && langParam !== 'ar') return;
    void i18n.changeLanguage(langParam);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = langParam;
      document.documentElement.dir = langParam === 'ar' ? 'rtl' : 'ltr';
      document.body.setAttribute('dir', langParam === 'ar' ? 'rtl' : 'ltr');
      try { localStorage.setItem(STORAGE_KEY, langParam); } catch { /* ignore storage errors in restricted environments */ }
    }
  }, [langParam, i18n]);

  const isSignupPath = pathname.endsWith('/signup');
  useEffect(() => {
    if (!isSignupPath) {
      sessionExpiredToastShown.current = false;
      return;
    }
    if (searchParams.get('reason') !== 'session_expired') return;
    if (sessionExpiredToastShown.current) return;
    sessionExpiredToastShown.current = true;
    addToast({
      variant: 'info',
      title: t('auth.sessionExpired.title', { defaultValue: 'Verification session expired' }),
      description: t('auth.sessionExpired.description', {
        defaultValue: 'Please enter your phone number again to request a new code.',
      }),
    });
    const next = new URLSearchParams(searchParams);
    next.delete('reason');
    const qs = next.toString();
    const l = langParam === 'ar' || langParam === 'en' ? langParam : 'en';
    navigate({ pathname: `/${l}/signup`, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [isSignupPath, searchParams, navigate, addToast, t, langParam]);

  if (authLoading) return <LoadingBlock className="min-h-screen" />;

  if (langParam !== 'en' && langParam !== 'ar') {
    const parts = pathname.split('/').filter(Boolean);
    const segment = parts[1] || 'main-brokerage';
    return <Navigate to={`/${getStoredLocale()}/${segment}`} replace />;
  }

  return <Outlet />;
}
